import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/react";

import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import { useMachine } from "@/_zustand/machine";
import { useProductStore } from "@/_zustand/store";
import { useOrder } from "@/_zustand/order";
import { useAuth } from "@/_zustand/auth";
import { useVerificarPagoMP, useReembolsarMP } from "@/hooks/useMercadoPago";
import { useCreateOrder } from "@/hooks/useTransbank";
import { mapCartForOrder, buildAppliedDiscount } from "@/lib";
import {
  classifyMpReturn,
  MP_VERIFY_TIMEOUT_MS,
  MP_MSG_ABANDONO,
  MP_MSG_RECHAZADO,
  MP_MSG_TIMEOUT_PREFIX,
} from "@/lib/mpReturn";
import {
  leerSnapshotMP,
  limpiarSnapshotMP,
  MP_TOTAL_TOLERANCIA,
} from "@/lib/mpCheckout";

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const MercadoPagoReturnPage = () => {
  const navigate = useNavigate();
  const { getCompanyRoute } = useCompanyFlow();
  const query = useQuery();
  const { machineSelected, addressMachine, machine } = useMachine();
  const {
    calculateTotals,
    products,
    buyOrder: storeBuyOrder,
    setFinishOrder,
    descuento_id,
    descuento_data,
  } = useProductStore();
  const { setOrder } = useOrder();
  const { user, incrementTotalPedidos } = useAuth();
  const { mutateAsync: verificarPagoMP } = useVerificarPagoMP();
  const { mutateAsync: reembolsarMP } = useReembolsarMP();
  const { mutateAsync: createOrder } = useCreateOrder();
  const hasProcessed = useRef(false);
  // Refs (no deps de efecto) para el AbortController/timeout de la
  // verificación en curso. useCompanyFlow().getCompanyRoute no está
  // memoizado, así que el efecto principal se re-ejecuta en cada render
  // posterior (p. ej. al hacer setFase). Si el abort/cleanup viviera en el
  // "return" de ESE efecto, cada re-render abortaría la verificación recién
  // iniciada antes de que la mutation llegara a ejecutarse. Por eso el
  // abort real sólo vive en el efecto de desmontaje de abajo.
  const controllerRef = useRef(null);
  const timeoutIdRef = useRef(null);

  // Clasificación sincrónica en el primer render: garantiza que la rama
  // "abandono" nunca pinte el heading "Validando la compra, por favor
  // espere!" ni de forma intermitente (criterio 2 / flash de UI).
  const [classification] = useState(() => classifyMpReturn(query));
  const [fase, setFase] = useState(
    classification.kind === "verificar" ? "verificando" : "clasificando",
  );
  const [timeoutInfo, setTimeoutInfo] = useState(null);

  const reembolsarPago = useCallback(
    (paymentId, message) => {
      reembolsarMP({ paymentId })
        .then((resp) => {
          if (resp?.status !== "success") {
            // Riesgo financiero alto: el pago quedó aprobado, la orden falló, Y
            // el reembolso automático tampoco se pudo confirmar — hay un cargo
            // real sin producto ni reembolso. Requiere intervención manual.
            Sentry.captureMessage("Reembolso de Mercado Pago no confirmado", {
              level: "error",
              tags: { financial_risk: true, payment_flow: "mercadopago" },
              extra: { paymentId, respMessage: resp?.message },
            });
          }
          toast.error(
            message ||
              "No había stock disponible, tu pago fue reembolsado automáticamente",
          );
        })
        .catch((error) => {
          Sentry.captureException(error, {
            level: "error",
            tags: { financial_risk: true, payment_flow: "mercadopago" },
            extra: { paymentId },
          });
          toast.error(
            "No había stock disponible. Contacta soporte para el reembolso.",
          );
        })
        .finally(() => {
          navigate(getCompanyRoute("cart"), { replace: true });
        });
    },
    [reembolsarMP, navigate, getCompanyRoute],
  );

  const crearPedido = useCallback(
    (data, externalReference, storeId) => {
      // Leer el estado más reciente del store (no el destructurado en el render
      // inicial) para el fallback, igual que OneClick.jsx — calculateTotals()
      // corrió de forma asíncrona antes de que verificarPagoMP resolviera.
      const { total: storeTotal, discount: storeDiscount } =
        useProductStore.getState();
      const mpAmount = Number(data.pago?.transaction_amount);

      // CA-12: verificación de solo-referencia contra el snapshot guardado al
      // iniciar el pago (Bloque C de MercadoPagoButton.jsx). El cargo ya
      // ocurrió a esta altura, así que se reporta pero NUNCA bloquea
      // createOrder (abortar dejaría al cliente cobrado y sin pedido ni QR).
      // Si no hay snapshot (otra pestaña, TTL vencido, buyOrder distinto) se
      // degrada en silencio, sin falsos positivos (caso borde 12/14).
      const snapshot = leerSnapshotMP(externalReference);
      if (snapshot && Number.isFinite(mpAmount)) {
        const diferenciaSnapshot = mpAmount - snapshot.total;
        if (Math.abs(diferenciaSnapshot) > MP_TOTAL_TOLERANCIA) {
          Sentry.captureMessage(
            "Monto cobrado por Mercado Pago distinto al total del checkout",
            {
              level: "error",
              tags: { financial_risk: true, payment_flow: "mercadopago" },
              extra: {
                externalReference,
                mpAmount,
                totalSnapshot: snapshot.total,
                diferencia: diferenciaSnapshot,
              },
            },
          );
        }
      }

      let dataSend = {
        appPlatform: "web",
        userId: user.usuario_id,
        cart: mapCartForOrder(products, storeId),
        storeId: storeId,
        total:
          Number.isFinite(mpAmount) && mpAmount > 0
            ? mpAmount
            : Math.max(0, storeTotal - storeDiscount),
        buyOrder: externalReference,
        appliedDiscount: buildAppliedDiscount(descuento_data, descuento_id),
        // El backend arma transactionDetail con la misma forma que Webpay al verificar el pago
        transactionDetail: data.transactionDetail,
      };

      createOrder(dataSend)
        .then((resp) => {
          if (resp.status === "success") {
            setOrder({
              ...resp,
              pedido_total: dataSend.total,
              maquina_direccion: addressMachine,
              maquina_telefono: machine?.maquina_telefono,
              usuario_correo: user.usuario_correo,
            });
            setFinishOrder(storeId);
            incrementTotalPedidos();
            limpiarSnapshotMP();
            return navigate(getCompanyRoute("order"), { replace: true });
          }

          return reembolsarPago(data.pago.id, resp.message);
        })
        .catch(() => {
          reembolsarPago(data.pago.id, "Error al realizar el pedido");
        });
    },
    [
      user,
      products,
      descuento_data,
      descuento_id,
      createOrder,
      setOrder,
      addressMachine,
      machine,
      setFinishOrder,
      incrementTotalPedidos,
      navigate,
      getCompanyRoute,
      reembolsarPago,
    ],
  );

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    calculateTotals(machineSelected);

    const { kind, paymentId, externalReference: classifiedRef } = classification;

    // Rama abandono: ausencia total de señal de cobro (sin payment_id, sin
    // status/collection_status significativo). Vía rápida deliberada: nunca
    // llama a verificarPago/createOrder/reembolsar, ni exige máquina ni
    // external_reference — no hay nada que verificar ni reversar.
    if (kind === "abandono") {
      sessionStorage.removeItem("mp_buyOrder");
      sessionStorage.removeItem("mp_storeId");
      limpiarSnapshotMP();
      toast.success(MP_MSG_ABANDONO);

      // Instrumentación (criterio 9): marca de tiempo medible sin depender de
      // apreciación visual — performance.now() en un documento recién
      // navegado equivale al tiempo desde el inicio de la navegación a
      // /mp-return.
      const ms = Math.round(performance.now());
      console.info("[MP] abandono resuelto en", ms, "ms");
      Sentry.addBreadcrumb({
        category: "mercadopago",
        message: "mp_return_abandono",
        data: { ms },
      });

      navigate(getCompanyRoute("cart"), { replace: true });
      return;
    }

    if (kind === "rechazado") {
      toast.error(MP_MSG_RECHAZADO);
      navigate(getCompanyRoute("cart"), { replace: true });
      return;
    }

    // kind === "verificar": camino existente, sin cambios funcionales, con
    // AbortController + timeout de cliente agregado.
    const externalReference =
      classifiedRef || storeBuyOrder || sessionStorage.getItem("mp_buyOrder");

    if (!externalReference) {
      toast.error("No se pudo identificar el pago de Mercado Pago");
      return navigate(getCompanyRoute("cart"), { replace: true });
    }

    // machineSelected es number (viene de useMachine); el fallback de
    // sessionStorage siempre es string, por lo que hay que castearlo para que
    // setFinishOrder (comparación estricta contra maquina_id) funcione igual.
    const storeId =
      machineSelected || Number(sessionStorage.getItem("mp_storeId")) || null;

    if (!storeId) {
      toast.error("No se pudo identificar la máquina de la compra");
      return navigate(getCompanyRoute("cart"), { replace: true });
    }

    setFase("verificando");

    const controller = new AbortController();
    controllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), MP_VERIFY_TIMEOUT_MS);
    timeoutIdRef.current = timeoutId;

    // Nombres mixtos (snake_case + camelCase) intencionales: coinciden tal
    // cual con el contrato del backend (Sima-apirest/docs/MERCADOPAGO.md).
    verificarPagoMP({
      external_reference: externalReference,
      paymentId,
      signal: controller.signal,
    })
      .then((resp) => {
        if (resp?.status === "success" && resp?.data?.pago?.status === "approved") {
          crearPedido(resp.data, externalReference, storeId);
        } else {
          const estado = resp?.data?.pago?.status;
          const mensaje =
            estado === "rejected"
              ? MP_MSG_RECHAZADO
              : estado === "pending" || estado === "in_process"
                ? "El pago está pendiente de confirmación"
                : resp?.message || "No se pudo confirmar el pago";
          toast.error(mensaje);
          return navigate(getCompanyRoute("cart"), { replace: true });
        }
      })
      .catch((error) => {
        if (error?.name === "AbortError") {
          // Timeout de cliente: nunca reembolsa ni marca la orden como
          // fallida — el webhook de MP + createOrder posterior siguen siendo
          // la fuente de verdad. Sólo cambia el copy a un estado seguro.
          // financial_risk sólo aplica si había paymentId (posible cobro);
          // sin él no hay cargo posible que investigar.
          if (paymentId) {
            Sentry.captureMessage("Timeout al verificar pago de Mercado Pago", {
              level: "warning",
              tags: { financial_risk: true, payment_flow: "mercadopago" },
              extra: { paymentId, externalReference },
            });
          }
          setTimeoutInfo({ externalReference });
          setFase("timeout");
          return;
        }

        // No sabemos si Mercado Pago ya cobró (falla de red/timeout al
        // verificar, no un pago rechazado) — si tenemos paymentId intentamos
        // reembolsar en vez de dejar un cobro sin confirmar ni reversar.
        if (paymentId) {
          return reembolsarPago(
            paymentId,
            "Error al verificar el pago. Si se realizó un cobro, fue reembolsado.",
          );
        }
        toast.error(
          "Error al verificar el pago con Mercado Pago. Si se realizó un cobro, contacta soporte con tu referencia: " +
            externalReference,
        );
        return navigate(getCompanyRoute("cart"), { replace: true });
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });

    // Sin cleanup aquí a propósito (ver comentario de controllerRef arriba):
    // el abort real de una verificación en curso sólo debe ocurrir en el
    // desmontaje genuino del componente (efecto separado más abajo), nunca
    // en un re-render espurio causado por deps inestables (getCompanyRoute).
  }, [
    classification,
    machineSelected,
    storeBuyOrder,
    calculateTotals,
    navigate,
    getCompanyRoute,
    verificarPagoMP,
    crearPedido,
    reembolsarPago,
  ]);

  // Cleanup de desmontaje genuino (deps [] a propósito): aborta la
  // verificación en curso sólo si el usuario navega fuera de esta página
  // antes de que resuelva o venza el timeout de cliente.
  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      if (controllerRef.current) controllerRef.current.abort();
    };
  }, []);

  if (fase === "timeout") {
    return (
      <section className="container my-5" style={{ flexGrow: 1 }}>
        <div className="row">
          <div className="col-12 mb-3 inter-regular text-center">
            <h3 className="text-black inter-semibold">
              {MP_MSG_TIMEOUT_PREFIX}{" "}
              {timeoutInfo?.externalReference || ""}
            </h3>
            <button
              type="button"
              className="order-btn-fill"
              onClick={() => navigate(getCompanyRoute("cart"), { replace: true })}
            >
              Volver al carrito
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Rama "abandono"/"clasificando" nunca llega a pintar este heading: el
  // useEffect navega en el mismo ciclo de montaje. Este render sólo se ve
  // para "verificando" (y brevemente para "clasificando" en el kind
  // "rechazado", que también navega de inmediato).
  if (fase !== "verificando") {
    return null;
  }

  return (
    <section className="container my-5" style={{ flexGrow: 1 }}>
      <div className="row">
        <div className="col-12 mb-3 inter-regular text-center">
          <h3 className="text-black inter-semibold">
            Validando la compra, por favor espere!
          </h3>
          <p className="text-black">Esto puede tomar hasta 10 segundos.</p>
          <Spin tip="Verificando..." size="large">
            <div style={{ padding: 50, borderRadius: 4 }} />
          </Spin>
        </div>
      </div>
    </section>
  );
};
